import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';

const STORAGE_KEY = 'lab21g.cart.v1';
const ASSISTED_PRICE = 99;

const CartContext = createContext(null);

const readStorage = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

const writeStorage = (items) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (e) {
    // storage unavailable; cart will reset on refresh
  }
};

const makeLineId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(readStorage);

  useEffect(() => { writeStorage(items); }, [items]);

  const addItem = useCallback(({ sku, name, price, widgetFamily, assistedDeployment = false }) => {
    setItems((prev) => [...prev, {
      lineId: makeLineId(),
      sku,
      name,
      price,
      widgetFamily: widgetFamily || null,
      assistedDeployment,
    }]);
  }, []);

  const removeItem = useCallback((lineId) => {
    setItems((prev) => prev.filter((i) => i.lineId !== lineId));
  }, []);

  const toggleAssisted = useCallback((lineId) => {
    setItems((prev) => prev.map((i) => (
      i.lineId === lineId ? { ...i, assistedDeployment: !i.assistedDeployment } : i
    )));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const total = useMemo(() => items.reduce(
    (sum, i) => sum + i.price + (i.assistedDeployment ? ASSISTED_PRICE : 0),
    0,
  ), [items]);

  const itemCount = useMemo(() => items.reduce(
    (n, i) => n + 1 + (i.assistedDeployment ? 1 : 0),
    0,
  ), [items]);

  const value = useMemo(() => ({
    items, addItem, removeItem, toggleAssisted, clearCart,
    total, itemCount, assistedPrice: ASSISTED_PRICE,
  }), [items, addItem, removeItem, toggleAssisted, clearCart, total, itemCount]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside <CartProvider>');
  return ctx;
};
